import type React from 'react'
import { useEffect, useState, useCallback } from 'react'
import type { IOrder } from '../../../../Model/index.js'
import { tracingAxios } from '../../../../Repository/Sentry.js'
import { Environment } from '../../../../environment.js'
import type { BaseModalProps } from '../../../Modal/BaseModal.js'
import { BaseModal } from '../../../Modal/BaseModal.js'
import Actions from '../../../../Flux/index.js'
import { orderSlice } from '../../../../Flux/Slice/Order/OrderSlice.js'
import { useDispatch, useSelector } from 'react-redux'
import { addMessage } from '../../../../Flux/Slice/Application/ApplicationSlice.js'
import { loadBilling, selectBillingCreditItems } from '../../../../Flux/Slice/Billing/BillingSlice.js'
import { errorMessage } from '../../../../Model/Message/IMessage.js'
import { useGetAuthenticatedUser, useGetIntermediatesStatusSelector, useGetSamplePoolSelector } from 'Flux/Selector/index.js'
import { intermediatesExpired } from '../../../../Util/IntermediatesLifetime.ts'
import { hasActiveSubscription } from 'Model/User/IUser.js'
import type { PostprocessingCost } from '../../../../Generated/PostprocessingCost.ts'
import type { CheckoutResponse } from '../../../../Generated/CheckoutResponse.ts'
import { QualityFilterLossWarning } from './PipelineInformation/QualityFilterLossWarning.tsx'

export interface StartPostprocessingModalProps {
  order: IOrder
  excludedItems?: string[]
}

export const StartPostprocessingModal: React.FC<StartPostprocessingModalProps & BaseModalProps> = ({
  isOpen,
  onClose,
  order,
  excludedItems
}) => {
  const [creditCost, setCreditCost] = useState<PostprocessingCost | null>(null)
  const [purchasing, setPurchasing] = useState(false)
  const [refreshingCredits, setRefreshingCredits] = useState(false)
  const dispatch = useDispatch()
  const user = useGetAuthenticatedUser()
  const expired = intermediatesExpired(useGetIntermediatesStatusSelector(), new Date())
  const samplePools = useGetSamplePoolSelector()
  const poolItems = samplePools === 'LOADING' ? [] : samplePools.flatMap((sp) => sp.samples)

  // Credit items (the purchasable credit packs) are shared billing state — read them from the slice
  // rather than refetching per modal. The per-order credit-cost below stays a direct fetch.
  const creditItems = useSelector(selectBillingCreditItems)
  const creditItem = creditItems[0] ?? null

  // Stable key so the cost is refetched whenever the set of deselected samples changes.
  const excludedKey = (excludedItems ?? []).join(',')

  const fetchCreditInfo = useCallback(
    async (showRefreshState = false) => {
      if (showRefreshState) {
        setRefreshingCredits(true)
      }

      try {
        // S2-995: ask for the exact cost given the current exclusions (reflects the quality filter
        // too), instead of the full-order estimate, so the displayed number matches what's charged.
        const params = new URLSearchParams()
        ;(excludedItems ?? []).forEach((id) => params.append('excluded', id))
        const creditCostRes = await tracingAxios.get<PostprocessingCost>(
          `${Environment.API_URL}/api/orders/${order.id}/postprocessing-cost?${params.toString()}`,
          { withCredentials: true }
        )
        setCreditCost(creditCostRes.data)
      } catch (e) {
        console.error('Failed to load credit information', e)
        dispatch(addMessage(errorMessage('Failed to load credit information. Please try again.')))
      } finally {
        if (showRefreshState) {
          setRefreshingCredits(false)
        }
      }
    },
    [dispatch, order.id, excludedKey]
  )

  useEffect(() => {
    if (!isOpen) return
    void fetchCreditInfo()
    // Lazily populate shared billing data if the user hasn't visited the billing page yet.
    if (creditItems.length === 0) {
      const includeCredits = !!(user && (hasActiveSubscription(user.org) || user.org.chargebeeCustomerId))
      dispatch(loadBilling({ includeCredits }))
    }
  }, [isOpen, fetchCreditInfo, creditItems.length, dispatch, user])

  const handleConfirm = () => {
    dispatch(
      Actions[orderSlice.name].submitPostprocessingRequest({
        orderId: order.id,
        excludedItems: excludedItems ?? []
      })
    )
    onClose()
  }

  const handlePurchaseCredits = async () => {
    if (!creditItem || !creditCost || creditCost.currentBalance === null || creditCost.currentBalance === undefined) return
    setPurchasing(true)
    try {
      const deficit = creditCost.creditCost - (creditCost.currentBalance || 0)
      const res = await tracingAxios.post<CheckoutResponse>(
        `${Environment.API_URL}/v1/chargebee/recharge`,
        {
          sampleCreditItemPriceId: creditItem.id,
          sampleQuantity: deficit,
          redirectUrl: `${window.location.origin}/order/${order.id}`
        },
        { withCredentials: true }
      )
      window.location.href = res.data.url
    } catch (e: unknown) {
      const msg =
        e instanceof Error
          ? ((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? e.message)
          : 'Failed to start credit purchase'
      dispatch(addMessage(errorMessage(msg)))
      setPurchasing(false)
    }
  }

  const handleRefreshCredits = async () => {
    await fetchCreditInfo(true)
  }

  if (!isOpen || !creditCost) return null

  const balance = creditCost.currentBalance
  const hasBalance = balance !== null && balance !== undefined
  const deficit = hasBalance ? creditCost.creditCost - balance : 0

  return (
    <BaseModal isOpen={isOpen} onClose={onClose}>
      <div className="modal modal-open">
        <div className="modal-box w-full max-w-lg">
          <h3 className="text-lg font-bold">Start Postprocessing</h3>
          <p className="mt-2">Preprocessing is complete. Starting postprocessing will create the result table from your samples.</p>
          <QualityFilterLossWarning items={poolItems} excludedItems={excludedItems} />

          <div className="mt-4 bg-base-200 rounded-lg p-4 space-y-2">
            {excludedItems && excludedItems.length > 0 && (
              <div className="flex justify-between">
                <span>Excluded samples:</span>
                <span className="font-semibold">{excludedItems.length}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span>Samples to process:</span>
              <span className="font-semibold">{creditCost.sampleCount}</span>
            </div>
            <div className="flex justify-between">
              <span>Credit cost:</span>
              <span className="font-semibold">{creditCost.creditCost} credits</span>
            </div>
            {creditCost.alreadyCharged && <p className="text-xs text-gray-500">This order was already paid for — resubmitting is free.</p>}
            <div className="divider my-1" />
            {hasBalance && (
              <div className="flex justify-between">
                <span>Your balance:</span>
                <span className={`font-semibold ${creditCost.sufficient ? 'text-success' : 'text-error'}`}>{balance} credits</span>
              </div>
            )}
            {!creditCost.sufficient && (
              <div className="mt-2 space-y-3">
                {hasBalance && <p className="text-error text-sm">Insufficient credits. You need {deficit} more credits.</p>}

                <div className="rounded-md bg-base-100 p-3 text-sm">
                  <p className="mb-2">Recharged credits may take a moment to be updated.</p>
                  <button className="btn btn-outline btn-sm" onClick={handleRefreshCredits} disabled={refreshingCredits}>
                    {refreshingCredits ? <span className="loading loading-spinner loading-sm" /> : 'Refresh'}
                  </button>
                </div>

                {creditItem && (
                  <button className="btn btn-primary btn-sm" onClick={handlePurchaseCredits} disabled={purchasing}>
                    {purchasing ? <span className="loading loading-spinner loading-sm" /> : `Purchase ${deficit} Credits`}
                  </button>
                )}

                <p className="text-xs text-gray-500">
                  Or visit the{' '}
                  <a href="/billing" className="link link-primary">
                    Billing page
                  </a>{' '}
                  for more options.
                </p>
              </div>
            )}
          </div>

          <p className="mt-4 text-sm text-gray-500">
            Depending on the dataset size, postprocessing can take up to 24 hours. You will receive an email notification when results are
            ready.
          </p>

          {creditCost.sampleCount === 0 && (
            <p className="mt-3 text-error text-sm">No samples are eligible for postprocessing — there is nothing to start.</p>
          )}

          {expired && (
            <p className="mt-3 text-error text-sm" data-testid="postprocessing-intermediates-expired">
              The preprocessing intermediates of this order have been deleted, so postprocessing cannot start. Re-run preprocessing first.
            </p>
          )}

          <div className="modal-action">
            <button
              className="btn btn-primary"
              disabled={expired || !creditCost.sufficient || creditCost.sampleCount === 0}
              onClick={handleConfirm}
            >
              Confirm & Start
            </button>
            <button className="btn btn-outline" onClick={() => onClose()}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </BaseModal>
  )
}
